import React, { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { UserContext } from "@/types/user";
import sdk from "@farcaster/miniapp-sdk";
import style from "./style.module.scss";
import { MINIAPP_SHARE_URL } from "@/constants/url";
import { SHARE_TEXT } from "@/constants/share";
import { MINIAPP_FID } from "@/constants/fid";

interface RewardDropdownProps {
  buttonClass?: string;
  loading: boolean;
  showSuccessModal: boolean;
  claimed: boolean | undefined;
  user: UserContext | undefined;
  isWhitelistOver: boolean;
  handleClaim: () => void;
  handleCloseModal: () => void;
}

const DELAY = import.meta.env.PROD ? 3000 : 1000;

const RewardDropdown: React.FC<RewardDropdownProps> = ({
  buttonClass,
  loading,
  handleClaim,
  showSuccessModal,
  handleCloseModal,
  claimed = false,
  user,
  isWhitelistOver,
}) => {
  const [open, setOpen] = useState(false);
  const [followed, setFollowed] = useState(false);
  const [followUnlocked, setFollowUnlocked] = useState(false);
  const [claimUnlocked, setClaimUnlocked] = useState(false);

  const handleShare = async () => {
    await sdk.actions.composeCast({
      text: SHARE_TEXT,
      embeds: [MINIAPP_SHARE_URL],
    });
    setTimeout(() => setFollowUnlocked(true), DELAY);
  };

  const handleFollow = async () => {
    await sdk.actions.viewProfile({
      fid: MINIAPP_FID,
    });
    setFollowed(true);
    setTimeout(() => setClaimUnlocked(true), DELAY);
  };

  return (
    <div className={style.wrapper}>
      {/* ORIGINAL BUTTON */}
      <button
        className={buttonClass}
        disabled={isWhitelistOver}
        onClick={() => setOpen(!open)}
      >
        Join Whitelist
      </button>
      {/* DROPDOWN */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.25 }}
            className={style.dropdown}
          >
            {claimed ? (
              <>
                <button
                  onClick={handleShare}
                  className={`${style.task} ${style.active}`}
                >
                  Share
                </button>

                <button
                  onClick={handleFollow}
                  className={`${style.task} ${style.active}
                  }`}
                >
                  Follow
                </button>

                <button
                  className={`${style.task} ${style.active}
                  }`}
                >
                  Joined
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleShare}
                  className={`${style.task} ${style.active}`}
                >
                  Share
                </button>

                <button
                  onClick={handleFollow}
                  disabled={!followUnlocked || followed}
                  className={`${style.task} ${
                    followUnlocked ? style.active : style.locked
                  }`}
                >
                  Follow
                </button>

                <button
                  onClick={handleClaim}
                  disabled={!claimUnlocked || claimed}
                  className={`${style.task} ${
                    claimUnlocked ? style.active : style.locked
                  }`}
                >
                  {loading ? "Processing..." : "Join Whitelist"}
                </button>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showSuccessModal && (
          <>
            <motion.div
              className={style.modalOverlay}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.7 }}
              exit={{ opacity: 0 }}
              onClick={handleCloseModal}
            />
            <motion.div
              className={style.modal}
              initial={{ opacity: 0, scale: 0.85 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.85 }}
              transition={{ duration: 0.25 }}
            >
              <h2>You're Whitelisted 🎉 {user?.displayName}</h2>
              <p>Get ready — mint is coming soon.</p>

              <button
                className="button-flat"
                style={{ borderRadius: "4px" }}
                onClick={handleCloseModal}
              >
                Close
              </button>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RewardDropdown;
